import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useParams } from 'react-router-dom';
import useApiControl from './common/useApiControl';
import { SSEProvider, useSSEData, useSSETarget } from './common/useSseControl';
import CoveredControlArea from './components/ownerConsole/CoveredControlArea';
import OwnerConsoleRightPanel from './components/ownerConsole/OwnerConsoleRightPanel';
import OwnerConsoleStaticHeader from './components/ownerConsole/OwnerConsoleStaticHeader';
import { OWNER_CONSOLE_DISPLAY_CATEGORY, getOwnerConsoleDisplayMeta } from './components/ownerConsole/ownerConsoleDisplayMeta';
import useRoomChat from './components/room/useRoomChat';
import useRoomPhase, { getServerPhaseCommandFromSseData, SERVER_PHASE } from './components/room/useRoomPhase';

const SSE_BASE_URL = '/api/sse_updates';

function OwnerConsoleMobileScreen({ event, eventId, round, phaseContext, onRefresh, liveSummary, sseData, chat, review, reviewRefreshKey }) {
  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: '#f8fafc' }}>
      <CoveredControlArea mobile event={event} roomId={eventId} round={round} phaseState={phaseContext} onRefresh={onRefresh} liveSummary={liveSummary} sseData={sseData} chat={chat} review={review} reviewRefreshKey={reviewRefreshKey} />
    </Box>
  );
}

function OwnerConsoleDesktopScreen({ event, eventId, round, phaseContext, onRefresh, liveSummary, roundSummary, sseData, chat, resultSummary, review, reviewRefreshKey }) {
  return (
    <Box
      sx={{
        minHeight: '100dvh',
        bgcolor: '#020617',
        color: '#fff',
        display: 'grid',
        placeItems: 'center',
        p: { sm: 1.5, md: 2.5 },
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: 'min(95vw, 1920px)',
          aspectRatio: '16 / 9',
          maxHeight: '95dvh',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) clamp(286px, 18vw, 344px)',
          gridTemplateRows: 'clamp(132px, 15vw, 172px) minmax(0, 1fr)',
          gridTemplateAreas: `
            "header rightPanel"
            "control rightPanel"
          `,
          gap: { sm: 1, md: 1.5 },
          p: { sm: 1, md: 1.5 },
          boxSizing: 'border-box',
          borderRadius: { sm: 2, md: 4 },
          border: '1px solid rgba(148,163,184,0.2)',
          background:
            'radial-gradient(circle at 12% 10%, rgba(14,165,233,0.26), transparent 28%), linear-gradient(135deg, #020617 0%, #0f172a 54%, #111827 100%)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.42)',
        }}
      >
        <OwnerConsoleStaticHeader event={event} roomId={eventId} round={round} phaseState={phaseContext} liveSummary={liveSummary} roundSummary={roundSummary} />
        <CoveredControlArea event={event} roomId={eventId} round={round} phaseState={phaseContext} onRefresh={onRefresh} liveSummary={liveSummary} sseData={sseData} chat={chat} review={review} reviewRefreshKey={reviewRefreshKey} />
        <OwnerConsoleRightPanel event={event} round={round} liveSummary={liveSummary} roundSummary={roundSummary} phaseState={phaseContext} chat={chat} resultSummary={resultSummary} review={review} />
      </Box>

      {!event ? (
        <Typography variant="caption" sx={{ position: 'fixed', bottom: 8, color: 'rgba(255,255,255,0.56)' }}>
          イベント未取得のためプレースホルダー表示中
        </Typography>
      ) : null}
    </Box>
  );
}

function OwnerConsoleContent({ event, eventId, isMobile, round, roundSummary, onRefresh }) {
  const { sseData } = useSSEData() ?? {};
  const roundSummarySseData = useSSETarget('roundSummary');
  const [resultSummary, setResultSummary] = useState(null);
  const [displayResultRoundNumber, setDisplayResultRoundNumber] = useState(null);
  const [review, setReview] = useState(null);
  const [reviewRefreshKey, setReviewRefreshKey] = useState(0);
  const liveSummary = roundSummarySseData?.type === 'direct' &&
    roundSummarySseData.action === 'update' &&
    roundSummarySseData.target === 'roundSummary' &&
    Number(roundSummarySseData.payload?.round_id) === Number(round?.id)
    ? roundSummarySseData.payload
    : null;
  const { roomDisplayPhase, serverPhase, timePhase } = useRoomPhase({
    event,
    round,
    user: null,
    sseData,
  });
  const chatRoomId = event?.id ? Number(event.id) : null;
  const chat = useRoomChat({
    chatRoomId,
    sseData,
    limit: 30,
  });
  const phaseState = useMemo(
    () => getOwnerConsoleDisplayMeta({
      roomDisplayPhase: event && round ? roomDisplayPhase : null,
      fallbackMessage: event && round ? null : '状態取得中',
    }),
    [event, roomDisplayPhase, round]
  );
  const phaseContext = useMemo(
    () => ({
      ...phaseState,
      serverPhase,
      timePhase,
    }),
    [phaseState, serverPhase, timePhase]
  );
  const confirmedRoundNumber = Number(event?.confirmed_round_count);
  const resultSummaryRoundNumber = displayResultRoundNumber
    ?? (Number.isInteger(confirmedRoundNumber) && confirmedRoundNumber > 0 ? confirmedRoundNumber : round?.number);
  const resultSummaryParams = useMemo(
    () => ({ event_id: event?.id, number: resultSummaryRoundNumber }),
    [event?.id, resultSummaryRoundNumber]
  );
  const reviewParams = useMemo(
    () => ({ event_id: event?.id, number: round?.number }),
    [event?.id, round?.number]
  );
  const { executeRequest: fetchResultSummary } = useApiControl({
    action: '/get_round_result',
    params: resultSummaryParams,
    method: 'POST',
  });
  const { executeRequest: fetchReview } = useApiControl({
    action: '/get_round_result_review',
    params: reviewParams,
    method: 'POST',
  });

  const resolveResultSummaryRoundNumber = (payloadRoundNumber) => {
    const payloadNumber = Number(payloadRoundNumber);
    if (Number.isInteger(payloadNumber) && payloadNumber > 1) {
      return payloadNumber - 1;
    }

    if (Number.isInteger(confirmedRoundNumber) && confirmedRoundNumber > 0) {
      return confirmedRoundNumber;
    }

    const currentRoundNumber = Number(round?.number);
    return Number.isInteger(currentRoundNumber) && currentRoundNumber > 0 ? currentRoundNumber : null;
  };

  useEffect(() => {
    const commandPhase = getServerPhaseCommandFromSseData(sseData);
    if (commandPhase === SERVER_PHASE.CONFIRMED && sseData?.payload?.round_number) {
      setDisplayResultRoundNumber(resolveResultSummaryRoundNumber(sseData.payload.round_number));
    }
    if (commandPhase === SERVER_PHASE.RESULT_PENDING) {
      setReviewRefreshKey((prev) => prev + 1);
    }
  }, [sseData, event?.confirmed_round_count, round?.number]);

  useEffect(() => {
    if ([
      OWNER_CONSOLE_DISPLAY_CATEGORY.BETTING,
      OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_INPUT,
      OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_REVIEW,
      OWNER_CONSOLE_DISPLAY_CATEGORY.WAITING,
    ].includes(phaseContext.displayCategory)) {
      setDisplayResultRoundNumber(null);
    }
  }, [phaseContext.displayCategory, round?.id, round?.number]);

  useEffect(() => {
    if (phaseContext.displayCategory !== OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_REVIEW || !event?.id || !round?.number) {
      setReview(null);
      return;
    }

    let cancelled = false;
    fetchReview({
      event_id: event.id,
      number: round.number,
    }).then((result) => {
      if (!cancelled) {
        setReview(result);
      }
    }).catch(() => {
      if (!cancelled) {
        setReview(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [event?.id, round?.number, phaseContext.displayCategory, reviewRefreshKey, fetchReview]);

  useEffect(() => {
    const shouldLoadResultSummary = [
      OWNER_CONSOLE_DISPLAY_CATEGORY.ROUND_RESULT,
    ].includes(phaseContext.displayCategory);

    if (!event?.id || !resultSummaryRoundNumber || !shouldLoadResultSummary) {
      setResultSummary(null);
      return;
    }

    let cancelled = false;
    fetchResultSummary({
      event_id: event.id,
      number: resultSummaryRoundNumber,
    }).then((result) => {
      if (!cancelled) {
        setResultSummary(result);
      }
    }).catch((error) => {
      if (!cancelled) {
        console.error(error);
        setResultSummary(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [event?.id, resultSummaryRoundNumber, phaseContext.displayCategory, fetchResultSummary]);

  if (isMobile) {
    return (
      <OwnerConsoleMobileScreen
        event={event}
        eventId={eventId}
        round={round}
        phaseContext={phaseContext}
        onRefresh={onRefresh}
        liveSummary={liveSummary}
        sseData={sseData}
        chat={chat}
        review={review}
        reviewRefreshKey={reviewRefreshKey}
      />
    );
  }

  return (
    <OwnerConsoleDesktopScreen
      event={event}
      eventId={eventId}
      round={round}
      phaseContext={phaseContext}
      onRefresh={onRefresh}
      liveSummary={liveSummary}
      roundSummary={roundSummary}
      sseData={sseData}
      chat={chat}
      resultSummary={resultSummary}
      review={review}
      reviewRefreshKey={reviewRefreshKey}
    />
  );
}

export default function OwnerConsole() {
  const { eventId } = useParams();
  const theme = useTheme();
  const narrowViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const landscapePhoneViewport = useMediaQuery('(orientation: landscape) and (max-height: 600px) and (hover: none) and (pointer: coarse)');
  const isMobile = narrowViewport || landscapePhoneViewport;
  const [event, setEvent] = useState(null);
  const [round, setRound] = useState(null);
  const [roundSummary, setRoundSummary] = useState(null);

  const params = useMemo(
    () => ({
      room_id: eventId,
    }),
    [eventId]
  );

  const { data, error, loading, executeRequest } = useApiControl({
    action: '/get_event_context',
    params,
    method: 'POST',
  });

  useEffect(() => {
    if (!eventId) {
      return;
    }

    executeRequest().catch((requestError) => {
      console.error(requestError);
    });
  }, [eventId, executeRequest]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !eventId) {
        return;
      }

      executeRequest().catch((requestError) => {
        console.error('OwnerConsoleの画面復帰時再同期に失敗しました。', requestError);
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [eventId, executeRequest]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setEvent(data.event ?? null);
    setRound(data.round ?? null);
    setRoundSummary(data.round_summary ?? null);
  }, [data]);

  if (loading && !event) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: '#020617', color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, minHeight: '100dvh', boxSizing: 'border-box' }}>
        <Alert severity="error">オーナーコンソール用のイベント情報の取得に失敗しました</Alert>
      </Box>
    );
  }

  if (!event?.id) {
    return <OwnerConsoleContent event={event} eventId={eventId} isMobile={isMobile} round={round} roundSummary={roundSummary} onRefresh={executeRequest} />;
  }

  return (
    <SSEProvider url={`${SSE_BASE_URL}?index=${event.id}`}>
      <OwnerConsoleContent event={event} eventId={eventId} isMobile={isMobile} round={round} roundSummary={roundSummary} onRefresh={executeRequest} />
    </SSEProvider>
  );
}
